// src/FuelSubsection.jsx

import React, { useState, useEffect, useRef } from 'react';

export default function FuelSubsection({
  initialData = [],       // e.g. [{ tankName, fuelTypes, location, volume100, … }, …]
  onDataChange,           // callback to lift changes
  isEditMode = false
}) {
  const [tanks, setTanks] = useState(initialData);

  // Ref to skip notifying parent once immediately after syncing props → state
  const skipNextNotify = useRef(false);

  // 1️⃣ Sync incoming initialData into local state, but only when it truly differs
  useEffect(() => {
    const prev = JSON.stringify(tanks);
    const next = JSON.stringify(initialData);
    if (prev !== next) {
      setTanks(initialData);
      skipNextNotify.current = true;
    }
  }, [initialData]);

  // 2️⃣ Notify parent when tanks state changes (but skip exactly one after sync)
  useEffect(() => {
    if (skipNextNotify.current) {
      skipNextNotify.current = false;
      return;
    }
    if (JSON.stringify(tanks) !== JSON.stringify(initialData)) {
      onDataChange(tanks);
    }
  }, [tanks, initialData, onDataChange]);

  const handleFieldChange = (idx, field, value) => {
    setTanks(prev =>
      prev.map((tank, i) =>
        i === idx ? { ...tank, [field]: value } : tank
      )
    );
  };

  const addTank = () => {
    setTanks(prev => [
      ...prev,
      {
        tankName: '',
        fuelTypes: '',
        location: '',
        volume100: '',
        volume98: '',
        weight98: ''
      }
    ]);
  };

  const removeTank = idx => {
    setTanks(prev => prev.filter((_, i) => i !== idx));
  };

  return (
    <div className="mb-4">
      <h5 className="fw-bold">Fuel Tanks</h5>

      {tanks.map((tank, idx) => (
        <div className="border p-3 mb-3" key={tank._id || idx}>
          <div className="row g-2">
            {[
              ['tankName',  'Tank Name'],
              ['fuelTypes', 'Fuel Types'],
              ['location',  'Location'],
              ['volume100', 'Volume @100%'],
              ['volume98',  'Volume @98%'],
              ['weight98',  'Weight @98%']
            ].map(([field, label]) => (
              <div className="col-md-4" key={field}>
                <label className="form-label">{label}</label>
                <input
                  type={
                    field.startsWith('volume') || field.startsWith('weight')
                      ? 'number'
                      : 'text'
                  }
                  className="form-control"
                  value={tank[field] ?? ''}
                  onChange={e =>
                    handleFieldChange(idx, field, e.target.value)
                  }
                />
              </div>
            ))}

            {!isEditMode && (
              <div className="col-md-1 d-flex align-items-end">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => removeTank(idx)}
                >
                  &times;
                </button>
              </div>
            )}
          </div>
        </div>
      ))}

      {!isEditMode && (
        <button
          type="button"
          className="btn btn-secondary"
          onClick={addTank}
        >
          + Add Fuel Tank
        </button>
      )}
    </div>
  );
}
